/* eslint-disable no-dupe-class-members */
// eslint-disable-next-line no-unused-vars
const { pick, omit } = require('lodash');

// eslint-disable-next-line no-unused-vars
const { FILTER_TYPES, OPERATORS, ENTITY_TYPES, ROUTE_RESERVED_KEYWORDS } = require('../constants');
const { convertValuesToContributionExpression } = require('../utils/convertValuesToContributionExpression');
const convertValuesToString = require('../utils/convertValuesToString');
const getEntityIdWithPrefix = require('../utils/getEntityIdWithPrefix');

const FILTER_API_PARAMS = {
  SEARCH_EXP: 'search_exp',
  DATE: 'date',
  LETTER: 'letter',
  PAGE: 'page',
  PROFESSION_ID: 'profession_id',
  ORGANIZATION_TYPE_ID: 'organization_type_id',
  COUNTRY_ID: 'country_id',
  CITY_ID: 'city_id',
  CONTRIBUTION_EXPRESSION: 'contribution_exp',
  COMPOSER: 'work_creator_id',
  LANGUAGE: 'language_id',
  TICKET: 'has_tickets',
  WATCH_ONLINE: 'has_online',
  SURTITLE: 'surtitle_id',
  HIGHLIGHT_TYPE: 'highlight_type',
  WORK_TYPE_ID: 'work_type_id',
  WORK: 'work_id',
  BOOLEAN_SEARCH: 'expression',
  SINCE_YEAR: 'since_year',
  WORK_CREATOR_ID: 'work_creator_id',
  QUERY: 'query',
  COVER_DEBUT: 'cover_debut',
  ROLE_PROFESSION: 'credit',
  SCORING_PARAMS: 'scoring_params',
  JUMP_IN_EXPRESSION: 'jumpin_expression',
  [FILTER_TYPES.COVER_ROLE]: 'is_cover_role',
  [FILTER_TYPES.ROLE_DEBUT]: 'is_role_debut',
  [FILTER_TYPES.PRODUCER_DEBUT]: 'is_producer_debut',
  PRODUCTION_ID: 'production_id',
  SORT_CT: 'sort_ct',
  RENTALS: 'has_active_rental',
};

const API_PARAMS_FILTER_TYPE_MAP = {
  [FILTER_API_PARAMS.SEARCH_EXP]: [
    FILTER_TYPES.WHO,
    FILTER_TYPES.WHERE,
    FILTER_TYPES.WHAT,
    FILTER_TYPES.GENRE,
    FILTER_TYPES.STAGING_TYPE,
  ],
  [FILTER_API_PARAMS.DATE]: [FILTER_TYPES.DATE],
  [FILTER_API_PARAMS.LETTER]: [FILTER_TYPES.LETTER],
  [FILTER_API_PARAMS.PAGE]: [FILTER_TYPES.PAGE],
  [FILTER_API_PARAMS.SORT_CT]: [FILTER_TYPES.SORT_CT],
  [FILTER_API_PARAMS.PROFESSION_ID]: [FILTER_TYPES.PROFESSION],
  [FILTER_API_PARAMS.ORGANIZATION_TYPE_ID]: [FILTER_TYPES.ORGANIZATION_TYPE],
  [FILTER_API_PARAMS.COUNTRY_ID]: [FILTER_TYPES.COUNTRY],
  [FILTER_API_PARAMS.CITY_ID]: [FILTER_TYPES.CITY],
  [FILTER_API_PARAMS.LANGUAGE]: [FILTER_TYPES.LANGUAGE],
  [FILTER_API_PARAMS.SURTITLE]: [FILTER_TYPES.SURTITLE],
  [FILTER_API_PARAMS.HIGHLIGHT_TYPE]: [FILTER_TYPES.PERFORMANCE_HIGHLIGHT],
  [FILTER_API_PARAMS.TICKET]: [FILTER_TYPES.TICKET],
  [FILTER_API_PARAMS.WORK]: [FILTER_TYPES.WORK],
  [FILTER_API_PARAMS.WORK]: [FILTER_TYPES.WORK],
  [FILTER_API_PARAMS.WORK_TYPE_ID]: [FILTER_TYPES.WORK_TYPE],
  [FILTER_API_PARAMS.WATCH_ONLINE]: [FILTER_TYPES.WATCH_ONLINE],
  [FILTER_API_PARAMS.BOOLEAN_SEARCH]: [FILTER_TYPES.BOOLEAN_SEARCH],
  [FILTER_API_PARAMS.SINCE_YEAR]: [FILTER_TYPES.SINCE_YEAR],
  [FILTER_API_PARAMS.ROLE_PROFESSION]: [FILTER_TYPES.ROLE_PROFESSION],
  [FILTER_API_PARAMS.SCORING_PARAMS]: [FILTER_TYPES.SCORING_PARAMS],
  [FILTER_API_PARAMS.JUMP_IN_EXPRESSION]: [FILTER_TYPES.JUMP_IN_EXPRESSION],
  [FILTER_API_PARAMS.COVER_DEBUT]: [FILTER_TYPES.COVER_DEBUT, FILTER_TYPES.ROLE_DEBUT, FILTER_TYPES.PRODUCER_DEBUT],
  [FILTER_API_PARAMS.QUERY]: [FILTER_TYPES.QUERY],
  [FILTER_API_PARAMS.PRODUCTION_ID]: [FILTER_TYPES.PRODUCTION_ID],
  [FILTER_API_PARAMS.COMPOSER]: [FILTER_TYPES.COMPOSER],
  [FILTER_API_PARAMS.CONTRIBUTION_EXPRESSION]: [
    FILTER_TYPES.CONDUCTOR,
    FILTER_TYPES.CHOREOGRAPHER,
    FILTER_TYPES.ROLE,
    FILTER_TYPES.ENSEMBLE,
    FILTER_TYPES.DIRECTOR,
    FILTER_TYPES.CO_PRODUCER,
    FILTER_TYPES.VENUE,
    FILTER_TYPES.PRODUCER,
  ],
  [FILTER_API_PARAMS.RENTALS]: [FILTER_TYPES.RENTALS],
};

class FilterParamsTransformer {
  #extractRoleAndProfessionIds(filterValue) {
    if (!filterValue || !Array.isArray(filterValue)) {
      return {};
    }

    return filterValue.reduce(
      (acc, item) => {
        if (item?.entityType === ENTITY_TYPES.WORK_ROLE) {
          acc.work_role_id.push(item?.entity?.id);
        }

        if (item?.entityType === ENTITY_TYPES.PROFESSION) {
          acc.profession_id.push(item?.entity?.id);
        }

        return acc;
      },
      { work_role_id: [], profession_id: [] },
    );
  }

  #transformDateParamValue(value) {
    return {
      ...(value?.date_from && { date_from: value.date_from }),
      ...(value?.date_to && { date_to: value.date_to }),
    };
  }

  #transformCoverDebutParamValue(value) {
    if (!value || typeof value !== 'string') {
      return {};
    }

    return value?.split(OPERATORS.AND)?.reduce((acc, slug) => {
      if (!slug) {
        return acc;
      }

      return {
        ...acc,
        [FILTER_API_PARAMS[slug]]: true,
      };
    }, {});
  }

  #transformFilterValues({ filterType, filterValue, values, entityMap }) {
    switch (filterType) {
      case FILTER_TYPES.DATE: {
        return this.#transformDateParamValue(filterValue);
      }

      case FILTER_TYPES.SINCE_YEAR: {
        return filterValue?.[0]?.id;
      }

      case FILTER_TYPES.WHAT:
      case FILTER_TYPES.WHERE:
      case FILTER_TYPES.WHO:
      case FILTER_TYPES.GENRE:
        return {
          ...values,
          [filterType]: convertValuesToString({ values: filterValue }),
        };
      case FILTER_TYPES.ORGANIZATION_TYPE:
      case FILTER_TYPES.PROFESSION:
      case FILTER_TYPES.COUNTRY:
      case FILTER_TYPES.CITY:
      case FILTER_TYPES.LANGUAGE:
      case FILTER_TYPES.SURTITLE:
      case FILTER_TYPES.PERFORMANCE_HIGHLIGHT:
      case FILTER_TYPES.WORK:
      case FILTER_TYPES.WORK_TYPE:
      case FILTER_TYPES.COVER_DEBUT:
      case FILTER_TYPES.ROLE_PROFESSION:
      case FILTER_TYPES.SCORING_PARAMS:
      case FILTER_TYPES.JUMP_IN_EXPRESSION:
      case FILTER_TYPES.PRODUCTION_ID:
      case FILTER_TYPES.COMPOSER:
        return convertValuesToString({ values: filterValue, filterType });

      case FILTER_TYPES.CONDUCTOR:
      case FILTER_TYPES.DIRECTOR:
      case FILTER_TYPES.CHOREOGRAPHER:
      case FILTER_TYPES.ROLE:
      case FILTER_TYPES.PRODUCER:
      case FILTER_TYPES.ENSEMBLE:
      case FILTER_TYPES.CO_PRODUCER:
      case FILTER_TYPES.VENUE:
        return convertValuesToContributionExpression({
          values,
          filterType,
          filterValue,
          entityMap,
        });

      default: {
        return filterValue;
      }
    }
  }

  #transformSearchParamValues(filters = {}, page) {
    const value = Object.keys(filters)
      .reduce((acc, filterType) => {
        const filterValue = filters[filterType];

        if (filterValue?.length === 1) {
          const { entityType, entity } = page || {};

          if (entityType === filterValue?.[0]?.entityType && entity?.id === filterValue?.[0]?.entity?.id) {
            return acc;
          }
        }

        acc.push(convertValuesToString({ values: filterValue, forcePrefix: true }));

        return acc;
      }, [])
      .join('|');

    return value || null;
  }

  #transformContributionExpressionValues(values = {}) {
    if (Object.keys(values).length === 0) {
      return null;
    }

    return Object.entries(values)
      .map(([key, subObj]) =>
        Object.entries(subObj)
          .map(
            ([id, categories]) =>
              `${key}:${id}:${Object.entries(categories)
                .map(([cat, options]) => {
                  if (options.length === 0) {
                    return cat;
                  }

                  return `${cat}|${options.join(OPERATORS.AND)}`;
                })
                .join(OPERATORS.CONTRIBUTOR_SEPARATOR)}`,
          )
          .join(OPERATORS.CONTRIBUTION_EXP_OPERATOR),
      )
      .join(OPERATORS.CONTRIBUTION_EXP_OPERATOR);
  }

  #transformFilterParams(params) {
    return Object.keys(params).reduce((acc, key) => {
      const value = params[key];

      if (Array.isArray(value) || ['number', 'string', 'boolean'].includes(typeof value)) {
        acc[key] = value;
      } else if (typeof value === 'object' && value !== null) {
        return { ...acc, ...this.#transformFilterParams(value) };
      }

      return acc;
    }, {});
  }

  transformFilters({ filters, entityMap, page }) {
    const { entityType, entity, tab } = page || {};
    const contextEntity =
      [ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType) && entity?.id
        ? {
            ctx_entity: getEntityIdWithPrefix({ entity, entityType })?.id,
          }
        : {};

    if (Object.keys(filters).length === 0) {
      return {};
    }

    const filterParams = Object.keys(API_PARAMS_FILTER_TYPE_MAP).reduce((acc, param) => {
      const filterTypes = API_PARAMS_FILTER_TYPE_MAP[param] || [];
      const relevantFilters = pick(filters, filterTypes);
      let value = Object.keys(relevantFilters).reduce((values, filterType) => {
        const filterValue = relevantFilters[filterType];

        return this.#transformFilterValues({ filterType, filterValue, values, entityMap, page });
      }, {});

      if (param === FILTER_API_PARAMS.SEARCH_EXP) {
        value = this.#transformSearchParamValues(relevantFilters, page);
        return {
          ...acc,
          [param]: value,
        };
      }

      if (param === FILTER_API_PARAMS.CONTRIBUTION_EXPRESSION) {
        value = this.#transformContributionExpressionValues(value);
      }

      if (param === FILTER_API_PARAMS.COVER_DEBUT) {
        return {
          ...acc,
          ...this.#transformCoverDebutParamValue(value),
        };
      }

      if (param === FILTER_API_PARAMS.RENTALS && tab === ROUTE_RESERVED_KEYWORDS.rentals) {
        return {
          ...acc,
          [param]: true,
        };
      }

      return {
        ...acc,
        [param]: value,
      };
    }, contextEntity);

    return this.#transformFilterParams(filterParams);
  }
}

module.exports = FilterParamsTransformer;
